import {
    isValidElement,
    ReactNode,
    useCallback,
    useState,
    useMemo,
    Children,
    ChangeEvent,
} from "react"
import * as T from "trunx"
import cn from "classnames"

import { useToggleClassOnHover } from "ui-kit/components-branded/hooks/setHoverHandler"
import { useQuerySelector } from "ui-kit/components-branded/hooks/useQuerySelector"
import { useIsInitialRender } from "ui-kit/components-branded/FormControls/useIsInitialRender"
import { Icon } from "components"

function forEachRecursive(
    children: ReactNode,
    callback: (child: ReactNode, index: number) => void
) {
    Children.forEach(children, (child, index) => {
        if (typeof child === "object" && child && "props" in child && "children" in child.props) {
            callback(child, index)
            return forEachRecursive(child.props.children, callback)
        }
        callback(child, index)
    })
}

export function Select({
    className,
    onChange,
    children,
    id,
    highlightOnSelectValue,
    ...props
}: T.SelectProps & {
    highlightOnSelectValue?: boolean
}) {
    const [nodeId, getNode] = useQuerySelector(id)
    useToggleClassOnHover(() => getNode()?.parentElement?.parentElement ?? null, "is-hovered")
    const isMount = useIsInitialRender()
    const isInitialSelected = useMemo(() => {
        const initialValue = props.defaultValue || (isMount ? props.value : undefined)
        let result = false
        forEachRecursive(children, (node) => {
            if (!isValidElement(node)) return
            if (node.type !== "option") return
            if (node.props.value.toString() === initialValue) {
                const { disabled } = node.props
                result = !(typeof disabled !== "undefined" && disabled)
            }
        })
        return result
    }, [])
    const [isSelected, setIsSelected] = useState(isInitialSelected)
    const onChangeWithHandler = useCallback(
        (event: ChangeEvent<HTMLSelectElement>) => {
            setIsSelected(true)
            onChange && onChange(event)
        },
        [onChange]
    )
    return (
        <div className="blm-select__wrapper">
            <T.Select
                {...props}
                className={cn(className, "blm-select", {
                    "is-success": isSelected && highlightOnSelectValue,
                })}
                onChange={onChangeWithHandler}
                id={nodeId}
            >
                {children}
            </T.Select>
            <Icon type="arrow-down" size={24} button className="blm-dropdown__icon" />
        </div>
    )
}
